import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import moment from "moment";
import PropTypes from "prop-types";
import classNames from "classnames";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useClickOutside } from "hooks/useClickOutside";
import {
	BlockStack,
	Button,
	Checkbox,
	InlineStack,
	Text,
} from "@shopify/polaris";
import { formatDate } from "utils/dates";

import calendarIcon from "img/icons/misc/calendar-icon.svg";
import clearIMG from "img/icons/actions/close.svg";

import "./index.scss";
import "./advancedRangeDatepicker.scss";

function getSuggestedMonthBoundariesFromToday() {
		const month = moment().subtract(1, "month").startOf("month");
		return {
				month,
				start: month.clone().startOf("month").startOf("day"),
				end: month.clone().endOf("month").startOf("day"),
		};
}

export const HISTORICAL_SUGGESTED_RANGE_PREVIOUS_FULL_MONTH = "previousFullMonth";
export const HISTORICAL_SUGGESTED_RANGE_TRAILING_TO_TODAY = "trailingToToday";

function getHistoricalSuggestedHintBoundaries(preset) {
		if (preset === HISTORICAL_SUGGESTED_RANGE_TRAILING_TO_TODAY) {
				const end = moment().startOf("day");
				const start = moment()
						.startOf("day")
						.subtract(1, "month")
						.subtract(1, "day");
				return {
						start,
						end,
						leftAnchorMonth: start.clone().startOf("month"),
						rightAnchorMonth: end.clone().startOf("month"),
				};
		}
		const legacy = getSuggestedMonthBoundariesFromToday();
		const m = legacy.month.clone().startOf("month");
		return {
				start: legacy.start,
				end: legacy.end,
				leftAnchorMonth: m,
				rightAnchorMonth: m,
		};
}

function suggestedVisibleMonthKey(panelMonthDate) {
		return moment(panelMonthDate).startOf("month").format("YYYY-MM");
}

function suggestedMonthKey(smMoment) {
		return smMoment.clone().startOf("month").format("YYYY-MM");
}

function isSameSuggestedCalendarDay(cellDate, suggestedBoundaryMoment) {
		const a = moment(cellDate).startOf("day");
		const b = moment(suggestedBoundaryMoment).startOf("day");
		return a.format("YYYY-MM-DD") === b.format("YYYY-MM-DD");
}

const RANGE_LIKE_DAY_CLASS_TOKENS = new Set([
		"AdvancedRangeDatepicker__dayInRange",
		"AdvancedRangeDatepicker__dayRangeStart",
		"AdvancedRangeDatepicker__dayRangeEnd",
		"AdvancedRangeDatepicker__dayRangeSingle",
		"react-datepicker__day--in-range",
		"react-datepicker__day--in-selecting-range",
		"react-datepicker__day--range-start",
		"react-datepicker__day--range-end",
		"react-datepicker__day--selecting-range-start",
		"react-datepicker__day--selecting-range-end",
]);

function sanitizeDayClassNameForFooterDecouple(classNamesInput) {
		if (typeof classNamesInput !== "string" || !classNamesInput.trim()) {
				return classNamesInput;
		}
		return classNamesInput
				.split(/\s+/)
				.filter(Boolean)
				.filter((tok) => !RANGE_LIKE_DAY_CLASS_TOKENS.has(tok))
				.join(" ");
}

function getIndependentDualRangeDayClassName(
		date,
		pane,
		startDate,
		endDate,
		splitMarkersByPane,
) {
		const d = moment(date).startOf("day");

		if (splitMarkersByPane) {
				const sOk =
						startDate && !isNaN(startDate.getTime())
								? moment(startDate).startOf("day")
								: null;
				const eOk =
						endDate && !isNaN(endDate.getTime())
								? moment(endDate).startOf("day")
								: null;
				if (pane === "left") {
						if (!sOk || !d.isSame(sOk, "day")) {
								return undefined;
						}
						if (eOk && sOk.isSame(eOk, "day")) {
								return "AdvancedRangeDatepicker__dayRangeSingle";
						}
						return "AdvancedRangeDatepicker__dayRangeStart";
				}
				if (pane === "right") {
						if (!eOk || !d.isSame(eOk, "day")) {
								return undefined;
						}
						if (sOk && sOk.isSame(eOk, "day")) {
								return "AdvancedRangeDatepicker__dayRangeSingle";
						}
						return "AdvancedRangeDatepicker__dayRangeEnd";
				}
				return undefined;
		}

		if (!startDate || !endDate) {
				return undefined;
		}
		const s = moment(startDate).startOf("day");
		const e = moment(endDate).startOf("day");
		const sameDayRange = s.isSame(e, "day");

		if (sameDayRange) {
				return d.isSame(s, "day")
						? "AdvancedRangeDatepicker__dayRangeSingle"
						: undefined;
		}
		if (d.isSame(s, "day")) {
				return "AdvancedRangeDatepicker__dayRangeStart";
		}
		if (d.isSame(e, "day")) {
				return "AdvancedRangeDatepicker__dayRangeEnd";
		}
		if (d.isAfter(s) && d.isBefore(e)) {
				return "AdvancedRangeDatepicker__dayInRange";
		}
		return undefined;
}

function normalizeBoundaryDate(value) {
		if (value === undefined || value === null) {
				return undefined;
		}
		if (value instanceof Date) {
				return isNaN(value.getTime()) ? undefined : value;
		}
		if (typeof value === "number") {
				const d = new Date(value);
				return isNaN(d.getTime()) ? undefined : d;
		}
		if (value != null && typeof value.toDate === "function") {
				const d = value.toDate();
				return d instanceof Date && !isNaN(d.getTime()) ? d : undefined;
		}
		if (value.$d instanceof Date) {
				const d = value.$d;
				return isNaN(d.getTime()) ? undefined : d;
		}
		if (typeof value.valueOf === "function") {
				const n = value.valueOf();
				if (typeof n === "number" && !Number.isNaN(n)) {
						const d = new Date(n);
						return isNaN(d.getTime()) ? undefined : d;
				}
		}
		const parsed = new Date(value);
		return isNaN(parsed.getTime()) ? undefined : parsed;
}

function AdvancedRangeChevronPrev() {
	return (
			<svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden>
					<path
							d="M6.5 1L1.5 7l5 6"
							stroke="currentColor"
							strokeWidth="1.67"
							strokeLinecap="round"
							strokeLinejoin="round"
					/>
			</svg>
	);
}

function AdvancedRangeChevronNext() {
	return (
			<svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden>
					<path
							d="M1.5 1l5 6-5 6"
							stroke="currentColor"
							strokeWidth="1.67"
							strokeLinecap="round"
							strokeLinejoin="round"
					/>
			</svg>
	);
}

function AdvancedRangeChevronYear() {
	return (
			<svg
					className="AdvancedRangeDatepicker__paneYearChevron"
					width="10"
					height="6"
					viewBox="0 0 10 6"
					fill="none"
					aria-hidden
			>
					<path
							d="M1 1l4 4 4-4"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
					/>
			</svg>
	);
}

function AdvancedRangePaneHeader({
	monthDate,
	decreaseMonth,
	increaseMonth,
	decreaseYear,
	increaseYear,
	prevMonthButtonDisabled,
	nextMonthButtonDisabled,
	prevYearButtonDisabled,
	nextYearButtonDisabled,
	prevAriaLabel,
	nextAriaLabel,
	prevYearAriaLabel,
	nextYearAriaLabel,
	showSubtitle,
	subtitleLine,
	yearSelectAriaLabel,
	isYearGridOpen,
	onToggleYearGrid,
}) {
	const monthName = moment(monthDate).format("MMMM");
	const currentYear = monthDate.getFullYear();

	const onPrevNav = isYearGridOpen ? decreaseYear : decreaseMonth;
	const onNextNav = isYearGridOpen ? increaseYear : increaseMonth;
	const prevDisabled = isYearGridOpen
			? Boolean(prevYearButtonDisabled)
			: Boolean(prevMonthButtonDisabled);
	const nextDisabled = isYearGridOpen
			? Boolean(nextYearButtonDisabled)
			: Boolean(nextMonthButtonDisabled);
	const prevLabel = isYearGridOpen ? prevYearAriaLabel : prevAriaLabel;
	const nextLabel = isYearGridOpen ? nextYearAriaLabel : nextAriaLabel;

	return (
			<div className="AdvancedRangeDatepicker__paneHeader">
					<div className="AdvancedRangeDatepicker__paneHeaderToolRow">
							<button
									type="button"
									className="AdvancedRangeDatepicker__navBtn"
									onClick={onPrevNav}
									disabled={prevDisabled}
									aria-label={prevLabel}
							>
									<AdvancedRangeChevronPrev />
							</button>
							<div className="AdvancedRangeDatepicker__paneHeaderTitle">
									<span className="AdvancedRangeDatepicker__paneMonth">
											{monthName}
									</span>
									<span className="AdvancedRangeDatepicker__paneYearGroup">
											<button
													type="button"
													className="AdvancedRangeDatepicker__paneYearTrigger"
													onClick={onToggleYearGrid}
													aria-expanded={isYearGridOpen}
													aria-haspopup="dialog"
													aria-label={yearSelectAriaLabel}
											>
													<span className="AdvancedRangeDatepicker__paneYearTriggerValue">
															{currentYear}
													</span>
													<span
															className={classNames(
																	"AdvancedRangeDatepicker__paneYearTriggerChevron",
																	isYearGridOpen &&
																			"AdvancedRangeDatepicker__paneYearTriggerChevron--open",
															)}
													>
															<AdvancedRangeChevronYear />
													</span>
											</button>
									</span>
							</div>
							<button
									type="button"
									className="AdvancedRangeDatepicker__navBtn"
									onClick={onNextNav}
									disabled={nextDisabled}
									aria-label={nextLabel}
							>
									<AdvancedRangeChevronNext />
							</button>
					</div>
					{showSubtitle && subtitleLine ? (
							<div className="AdvancedRangeDatepicker__paneHeaderSub">
									{subtitleLine}
							</div>
					) : null}
			</div>
	);
}

function defaultRangeDraftStartDate() {
	return moment().startOf("day").toDate();
}

const AdvancedRangeDatepicker = (props) => {
	const {t} = useTranslation();
	const {
		onChange = () => {
		},
		label,
		triggerLabel,
		showFooter = true,
		resetButtonLabel,
		applyButtonLabel,
		instantApplyWhenNoFooter = true,
		prefix = (
				<img
						src={calendarIcon}
						alt=''
						width={17}
						height={19}
						style={{display: "block"}}
				/>
		),
		start,
		end,
		value,
		allowRange = true,
		placeholder,
		datepickerProps,
		isClearable = false,
		customFormatDate = formatDate,
		alignment = "left",
		labelDisabled,
		minDate: minDateProp,
		maxDate: maxDateProp,
		minDateOverride,
		maxDateOverride,
		disabled = false,
		inlineDropdownToggle = null,
		hideRangeModeToggle = false,
		filtersFooter = false,
		syncDualMonthNavigation = false,
		linkDualMonthViewToRangeStart = false,
		historicalRangeMode = false,
		historicalSuggestedRangePreset = HISTORICAL_SUGGESTED_RANGE_PREVIOUS_FULL_MONTH,
		restrictFutureDates = false,
		defaultOpenToPreviousMonth = false,
		defaultRangeToPreviousMonth = false,
		suggestedPreviousMonthRangeHints = false,
		clearEndOnIndependentLeftPick = false,
		separateCalendarSelection = false,
		onPickerActiveChange,
		splitRangePanes = false,
	} = props;

	const resolvedTriggerLabel =
			triggerLabel ?? label ?? t("datepicker.dateRange");
	const applySelectionInstantly =
			!showFooter &&
			instantApplyWhenNoFooter !== false;
	const resolvedResetButtonLabel =
			resetButtonLabel ??
			t("datepicker.reset", {
					defaultValue: "Reset",
			});
	const resolvedApplyFiltersButtonLabel =
			applyButtonLabel ??
			t("datepicker.applyFilter", {
					defaultValue: "Apply filter",
			});
	const resolvedApplyFooterButtonLabel =
			applyButtonLabel ?? t("datepicker.apply");

	const restrictFutureEffective = historicalRangeMode || restrictFutureDates;
	const defaultOpenPrevMonthEffective =
			historicalRangeMode || defaultOpenToPreviousMonth;
	const defaultRangePrevMonthEffective =
			historicalRangeMode || defaultRangeToPreviousMonth;
	const separateCalendarsEffective =
			historicalRangeMode || separateCalendarSelection;

	const {
		monthsShown: _dpMonthsShown,
		renderCustomHeader: _dpRenderCustomHeader,
		openToDate: _dpOpenToDate,
		calendarStartDay: _dpCalendarStartDay,
		showYearPicker: _datepickerShowYearPicker,
		minDate: _dpMinDate,
		maxDate: _dpMaxDate,
		...restDatepickerProps
	} = datepickerProps || {};

	const calendarStartDayMerged = _dpCalendarStartDay ?? 1;

	const {minDate, maxDate} = useMemo(() => {
			const rawMin =
					minDateProp !== undefined
							? minDateProp
							: minDateOverride !== undefined
									? minDateOverride
									: _dpMinDate;
			const rawMax =
					maxDateProp !== undefined
							? maxDateProp
							: maxDateOverride !== undefined
									? maxDateOverride
									: _dpMaxDate;
			return {
					minDate: normalizeBoundaryDate(rawMin),
					maxDate: normalizeBoundaryDate(rawMax),
			};
	}, [
			minDateProp,
			maxDateProp,
			minDateOverride,
			maxDateOverride,
			_dpMinDate,
			_dpMaxDate,
	]);

	const effectiveMaxDate = useMemo(() => {
			if (!restrictFutureEffective) {
					return maxDate;
			}
			const today = moment().startOf("day").toDate();
			if (!maxDate) {
					return today;
			}
			return moment(maxDate).isAfter(moment(today)) ? today : maxDate;
	}, [maxDate, restrictFutureEffective]);

	const [rangeMode, setRangeMode] = useState(() => allowRange !== false);
	const initialRangeMode = allowRange !== false;
	const [active, setActive] = useState(false);
	const prevPickerActiveNotifyRef = useRef(active);
	useEffect(() => {
			if (prevPickerActiveNotifyRef.current !== active) {
					onPickerActiveChange?.(active);
					prevPickerActiveNotifyRef.current = active;
			}
	}, [active, onPickerActiveChange]);
	const [renderDate, setRenderDate] = useState("");
	const [startDate, setStartDate] = useState(() => {
		if (value) {
			const d = new Date(value);
			return isNaN(d.getTime()) ? null : d;
		}
		if (start !== undefined && start !== null) {
			const d = new Date(start);
			return isNaN(d.getTime()) ? null : d;
		}
		if (start === null) {
				return null;
		}
		const dpInit = datepickerProps || {};
		const rawMinInit =
				minDateProp !== undefined
						? minDateProp
						: minDateOverride !== undefined
								? minDateOverride
								: dpInit.minDate;
		const minInit = normalizeBoundaryDate(rawMinInit);
		if (minInit) {
				return new Date(minInit.getTime());
		}
		if (filtersFooter) {
			return null;
		}
		return initialRangeMode ? defaultRangeDraftStartDate() : null;
	});
	const [endDate, setEndDate] = useState(() =>
			end ? new Date(end) : null,
	);
	const [rangeViewAnchor, setRangeViewAnchor] = useState(null);
	const [leftPanelMonth, setLeftPanelMonth] = useState(() =>
			moment().startOf("month").toDate(),
	);
	const [rightPanelMonth, setRightPanelMonth] = useState(() =>
			moment().startOf("month").add(1, "month").toDate(),
	);
	const [yearPickerOpenStart, setYearPickerOpenStart] = useState(false);
	const [yearPickerOpenEnd, setYearPickerOpenEnd] = useState(false);
	const [yearPickerOpen, setYearPickerOpen] = useState(false);
	const leftYearViewAnchorRef = useRef(null);
	const rightYearViewAnchorRef = useRef(null);
	const syncYearViewAnchorRef = useRef(null);
	const [leftPaneCalendarKey, setLeftPaneCalendarKey] = useState(0);
	const [rightPaneCalendarKey, setRightPaneCalendarKey] = useState(0);
	const [syncPaneCalendarKey, setSyncPaneCalendarKey] = useState(0);
	const [omitLeftInlineOpenToDate, setOmitLeftInlineOpenToDate] = useState(false);
	const [omitRightInlineOpenToDate, setOmitRightInlineOpenToDate] = useState(false);
	const [omitSyncInlineOpenToDate, setOmitSyncInlineOpenToDate] = useState(false);
	const leftYearGridRemountRef = useRef(false);
	const rightYearGridRemountRef = useRef(false);
	const syncYearGridRemountRef = useRef(false);

	useLayoutEffect(() => {
			let raf = 0;
			let anyYearGridRemount = false;
			const pulseOmitOpenToDate = (didRemountRef, setOmit) => {
					if (!didRemountRef.current) {
							return;
					}
					didRemountRef.current = false;
					setOmit(true);
					anyYearGridRemount = true;
			};
			pulseOmitOpenToDate(leftYearGridRemountRef, setOmitLeftInlineOpenToDate);
			pulseOmitOpenToDate(rightYearGridRemountRef, setOmitRightInlineOpenToDate);
			pulseOmitOpenToDate(syncYearGridRemountRef, setOmitSyncInlineOpenToDate);
			if (anyYearGridRemount) {
					raf = requestAnimationFrame(() => {
							setOmitLeftInlineOpenToDate(false);
							setOmitRightInlineOpenToDate(false);
							setOmitSyncInlineOpenToDate(false);
					});
			}
			return () => {
					cancelAnimationFrame(raf);
					setOmitLeftInlineOpenToDate(false);
					setOmitRightInlineOpenToDate(false);
					setOmitSyncInlineOpenToDate(false);
			};
	}, [leftPaneCalendarKey, rightPaneCalendarKey, syncPaneCalendarKey]);

	useEffect(() => {
		if (!active) {
			setYearPickerOpenStart(false);
			setYearPickerOpenEnd(false);
			setYearPickerOpen(false);
			setOmitLeftInlineOpenToDate(false);
			setOmitRightInlineOpenToDate(false);
			setOmitSyncInlineOpenToDate(false);
		}
	}, [active]);

	useEffect(() => {
		if (allowRange === false) {
			setRangeMode(false);
		}
	}, [allowRange]);

	const effectiveRange = allowRange !== false && rangeMode;
	const monthsShownDesired = effectiveRange
			? (_dpMonthsShown ?? 2)
			: 1;
	const wantsTwoMonthChrome =
			effectiveRange &&
			monthsShownDesired > 1 &&
			!datepickerProps?.renderCustomHeader;
	const useIndependentDualMonths =
			wantsTwoMonthChrome && !syncDualMonthNavigation;
	const decoupleRightPanelFromRangeStart =
			useIndependentDualMonths && !linkDualMonthViewToRangeStart;
	const strictCalendarSplit =
			effectiveRange &&
			useIndependentDualMonths &&
			decoupleRightPanelFromRangeStart &&
			separateCalendarsEffective;
	const suggestedRangeHintsMode = Boolean(
			(historicalRangeMode && filtersFooter && strictCalendarSplit) ||
					(suggestedPreviousMonthRangeHints &&
							decoupleRightPanelFromRangeStart),
	);
	const useSplitPaneSelection =
			decoupleRightPanelFromRangeStart &&
			(!filtersFooter || strictCalendarSplit);
	const useDualMonthRangeHeader =
			wantsTwoMonthChrome && syncDualMonthNavigation;
	const monthsShownEffective = monthsShownDesired;

	const splitRangePanesMode = Boolean(
			splitRangePanes &&
			effectiveRange &&
			useIndependentDualMonths &&
			decoupleRightPanelFromRangeStart,
	);
	const filtersFooterDecoupledDualRange = splitRangePanesMode;

	const prevActiveRef = useRef(false);
	const triggerInputRef = useRef(null);

	useLayoutEffect(() => {
		if (!useIndependentDualMonths || !active || prevActiveRef.current) {
				return;
		}
		const parentHasStart = start !== undefined && start !== null;
		const parentHasEnd = end !== undefined && end !== null;
		const usePrevMonthChrome =
				(defaultOpenPrevMonthEffective || defaultRangePrevMonthEffective) &&
				!parentHasStart &&
				!parentHasEnd;
		const prevMonthAnchor = moment()
				.subtract(1, "month")
				.startOf("month")
				.toDate();

		if (usePrevMonthChrome) {
				if (
						historicalSuggestedRangePreset ===
						HISTORICAL_SUGGESTED_RANGE_TRAILING_TO_TODAY
				) {
						const bundle = getHistoricalSuggestedHintBoundaries(
								HISTORICAL_SUGGESTED_RANGE_TRAILING_TO_TODAY,
						);
						setLeftPanelMonth(bundle.leftAnchorMonth.toDate());
						setRightPanelMonth(bundle.rightAnchorMonth.toDate());
				} else {
						setLeftPanelMonth(prevMonthAnchor);
						setRightPanelMonth(prevMonthAnchor);
				}
		} else if (filtersFooterDecoupledDualRange) {
				const hasAnyRangeValue = Boolean(
						startDate ||
						endDate ||
						(start !== undefined && start !== null) ||
						(end !== undefined && end !== null),
				);
				if (!hasAnyRangeValue) {
						const shouldUsePrev =
								defaultOpenPrevMonthEffective ||
								defaultRangePrevMonthEffective ||
								historicalRangeMode;
						const baseMonth = shouldUsePrev
								? prevMonthAnchor
								: moment().startOf("month").toDate();
						setLeftPanelMonth(baseMonth);
						setRightPanelMonth(baseMonth);
						if (filtersFooterDecoupledDualRange) {
								setLeftPaneCalendarKey((k) => k + 1);
								setRightPaneCalendarKey((k) => k + 1);
						}
						return;
				}
				const rawS =
						startDate ??
						(start ? new Date(start) : null);
				const rawE =
						endDate ??
						(end ? new Date(end) : null);
				const sOk =
						rawS && !isNaN(rawS.getTime()) ? rawS : null;
				const eOk =
						rawE && !isNaN(rawE.getTime()) ? rawE : null;
				let nextLeft;
				if (sOk) {
						nextLeft = moment(sOk).startOf("month").toDate();
				} else {
						nextLeft = moment().startOf("month").toDate();
				}
				setLeftPanelMonth(nextLeft);
				if (eOk) {
						setRightPanelMonth(moment(eOk).startOf("month").toDate());
				} else if (sOk) {
						setRightPanelMonth(
								moment(sOk).startOf("month").toDate(),
						);
				} else {
						setRightPanelMonth(
								moment(nextLeft).startOf("month").toDate(),
						);
				}
		} else {
				const s = startDate ?? (start ? new Date(start) : null);
				const e = endDate ?? (end ? new Date(end) : null);
				let left = moment().startOf("month").toDate();
				if (s && !isNaN(s.getTime())) {
						left = moment(s).startOf("month").toDate();
				}
				const leftM = moment(left).startOf("month");
				let right = leftM.clone().add(1, "month").toDate();
				if (
						decoupleRightPanelFromRangeStart &&
						e &&
						!isNaN(e.getTime())
				) {
						const endM = moment(e).startOf("month");
						if (endM.isAfter(leftM)) {
								right = endM.toDate();
						}
				}
				setLeftPanelMonth(left);
				setRightPanelMonth(right);
		}
		if (filtersFooterDecoupledDualRange) {
				setLeftPaneCalendarKey((k) => k + 1);
				setRightPaneCalendarKey((k) => k + 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		active,
		useIndependentDualMonths,
		filtersFooterDecoupledDualRange,
		decoupleRightPanelFromRangeStart,
		startDate,
		endDate,
		start,
		end,
		defaultOpenPrevMonthEffective,
		defaultRangePrevMonthEffective,
		historicalRangeMode,
		historicalSuggestedRangePreset,
	]);

	useEffect(() => {
		if (!useIndependentDualMonths) {
			prevActiveRef.current = active;
			return;
		}
		if (active && !prevActiveRef.current) {
			const parentHasStart = start !== undefined && start !== null;
			const parentHasEnd = end !== undefined && end !== null;
			const prevMonthAnchor = moment()
					.subtract(1, "month")
					.startOf("month")
					.toDate();

			if (
					defaultRangePrevMonthEffective &&
					!parentHasStart &&
					!parentHasEnd &&
					!suggestedRangeHintsMode
			) {
					const s0 = moment(prevMonthAnchor).startOf("day").toDate();
					const e0 = moment(prevMonthAnchor).endOf("month").toDate();
					setStartDate(s0);
					setEndDate(e0);
					setRenderDate(
							`${customFormatDate(s0)} - ${customFormatDate(e0)}`,
					);
			}
		}
		prevActiveRef.current = active;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		active,
		useIndependentDualMonths,
		decoupleRightPanelFromRangeStart,
		filtersFooter,
		defaultOpenPrevMonthEffective,
		defaultRangePrevMonthEffective,
		suggestedRangeHintsMode,
		start,
		end,
		customFormatDate,
	]);

	useEffect(() => {
		if (!useIndependentDualMonths || decoupleRightPanelFromRangeStart) {
			return;
		}
		setRightPanelMonth(
				moment(leftPanelMonth)
						.add(1, "month")
						.startOf("month")
						.toDate(),
		);
	}, [
		leftPanelMonth,
		useIndependentDualMonths,
		decoupleRightPanelFromRangeStart,
	]);

	const dismissPickerRef = useRef(() => {
		setActive(false);
	});

	const pickerRef = useClickOutside(() => {
		dismissPickerRef.current();
	});

	useEffect(() => {
		if (value) {
			const parsedValue = new Date(value);
			if (!isNaN(parsedValue)) {
				setStartDate(parsedValue);
			}
			return;
		}
		if (value === null) {
			setStartDate(null);
			return;
		}
		if (start !== undefined && start !== null) {
			const parsedStart = new Date(start);
			if (!isNaN(parsedStart)) {
				setStartDate(parsedStart);
			}
			return;
		}
		if (start === null) {
			setStartDate(null);
		}
	}, [value, start]);

	useEffect(() => {
		if (!effectiveRange) return;
		if (end) {
			const parsedEnd = new Date(end);
			if (!isNaN(parsedEnd.getTime())) {
				setEndDate(parsedEnd);
			}
		} else if (end === null || end === undefined) {
			setEndDate(null);
		}
	}, [end, effectiveRange]);

	useEffect(() => {
		if (effectiveRange) {
			setRangeViewAnchor(null);
		}
	}, [start, end, effectiveRange]);

	const rightCalendarMinDate = useMemo(() => {
		if (!decoupleRightPanelFromRangeStart) {
			return minDate;
		}
		if (strictCalendarSplit && startDate) {
				if (!minDate) {
						return moment(startDate).startOf("day").toDate();
				}
				return moment(startDate).isBefore(moment(minDate))
						? minDate
						: moment(startDate).startOf("day").toDate();
		}
		if (endDate || !startDate) {
			return minDate;
		}
		if (!minDate) {
			return startDate;
		}
		return moment(startDate).isAfter(moment(minDate))
				? startDate
				: minDate;
	}, [
		decoupleRightPanelFromRangeStart,
		endDate,
		startDate,
		minDate,
		strictCalendarSplit,
	]);

	const linkedAdjacentDualMonths =
			useIndependentDualMonths && !decoupleRightPanelFromRangeStart;

	const strictRightNavFloorMonth = useMemo(() => {
			if (filtersFooterDecoupledDualRange || !strictCalendarSplit) {
					return null;
			}
			return startDate
					? moment(startDate).startOf("month")
					: moment(leftPanelMonth).startOf("month");
	}, [
		filtersFooterDecoupledDualRange,
		strictCalendarSplit,
		startDate,
		leftPanelMonth,
	]);

	const userFilterDateFromProps = datepickerProps?.filterDate;

	const futureFilterDate = useCallback(
			(date) => {
					if (!restrictFutureEffective) {
							return true;
					}
					return !moment(date)
							.startOf("day")
							.isAfter(moment().startOf("day"));
			},
			[restrictFutureEffective],
	);

	const strictRightFilterDate = useCallback(
			(date) => {
					if (userFilterDateFromProps && !userFilterDateFromProps(date)) {
							return false;
					}
					if (!futureFilterDate(date)) {
							return false;
					}
					if (!startDate) {
							return false;
					}
					if (
							moment(date)
									.startOf("day")
									.isBefore(moment(startDate).startOf("day"))
					) {
							return false;
					}
					return true;
			},
			[userFilterDateFromProps, futureFilterDate, startDate],
	);

	const linkedAdjacentRightFilterDate = useCallback(
			(date) => {
				const firstRight = moment(rightPanelMonth).startOf("month");
				if (moment(date).startOf("day").isBefore(firstRight)) {
					return false;
				}
				if (userFilterDateFromProps) {
					return userFilterDateFromProps(date);
				}
				return true;
			},
			[rightPanelMonth, userFilterDateFromProps],
	);

	const independentDualLeftFilter = useCallback(
			(d) => {
					if (restrictFutureEffective || userFilterDateFromProps) {
							if (userFilterDateFromProps && !userFilterDateFromProps(d)) {
									return false;
							}
							return futureFilterDate(d);
					}
					const f = restDatepickerProps.filterDate;
					return f ? f(d) : true;
			},
			[
					restrictFutureEffective,
					userFilterDateFromProps,
					futureFilterDate,
					restDatepickerProps.filterDate,
			],
	);

	const independentDualRightFilter = useCallback(
			(d) => {
					if (strictCalendarSplit) {
							return strictRightFilterDate(d);
					}
					if (linkedAdjacentDualMonths) {
							return linkedAdjacentRightFilterDate(d);
					}
					if (restrictFutureEffective || userFilterDateFromProps) {
							if (userFilterDateFromProps && !userFilterDateFromProps(d)) {
									return false;
							}
							return futureFilterDate(d);
					}
					const f = restDatepickerProps.filterDate;
					return f ? f(d) : true;
			},
			[
					strictCalendarSplit,
					strictRightFilterDate,
					linkedAdjacentDualMonths,
					linkedAdjacentRightFilterDate,
					restrictFutureEffective,
					userFilterDateFromProps,
					futureFilterDate,
					restDatepickerProps.filterDate,
			],
	);

	const isIndependentDualDaySelectable = useCallback(
			(date, pane) => {
					const day = moment(date).startOf("day");
					const low = pane === "right" ? rightCalendarMinDate : minDate;
					const high = effectiveMaxDate;
					if (low) {
							const mLow = moment(low).startOf("day");
							if (day.isBefore(mLow)) {
									return false;
							}
					}
					if (high) {
							const mHigh = moment(high).startOf("day");
							if (day.isAfter(mHigh)) {
									return false;
							}
					}

					const filterFn =
							pane === "right" &&
							strictCalendarSplit &&
							!startDate
									? independentDualLeftFilter
									: pane === "right"
											? independentDualRightFilter
											: independentDualLeftFilter;
					return filterFn(date);
			},
			[
					minDate,
					rightCalendarMinDate,
					effectiveMaxDate,
					independentDualLeftFilter,
					independentDualRightFilter,
					strictCalendarSplit,
					startDate,
			],
	);

	const disableRightPanePrevMonthAdjacent = useMemo(() => {
		if (!linkedAdjacentDualMonths) {
			return false;
		}
		const l = moment(leftPanelMonth).startOf("month");
		const r = moment(rightPanelMonth).startOf("month");
		return r.isSame(l.clone().add(1, "month"));
	}, [linkedAdjacentDualMonths, leftPanelMonth, rightPanelMonth]);

	const handleLinkedRightMonthOrYearChange = useCallback(
			(d) => {
				const rightMonth = moment(d).startOf("month");
				const leftMonth = moment(leftPanelMonth).startOf("month");
				if (!rightMonth.isAfter(leftMonth)) {
					return;
				}
				setLeftPanelMonth(
						rightMonth.clone().subtract(1, "month").toDate(),
				);
			},
			[leftPanelMonth],
	);

	useEffect(() => {
		if (effectiveRange) {
			if (active) {
				return;
			}
			if (start && end) {
				setRenderDate(`${customFormatDate(start)} - ${customFormatDate(end)}`);
				return;
			}
			if (start && !end) {
				setRenderDate(customFormatDate(start));
				return;
			}
			if (startDate) {
				setRenderDate(
						endDate
								? `${customFormatDate(startDate)} - ${customFormatDate(endDate)}`
								: customFormatDate(startDate),
				);
				return;
			}
			setRenderDate("");
			return;
		}
		if (startDate) {
			setRenderDate(customFormatDate(startDate));
		} else if (value) {
			setRenderDate(customFormatDate(value));
		} else if (minDate) {
			setRenderDate(customFormatDate(new Date(minDate.getTime())));
		} else {
			setRenderDate("");
		}
	}, [
		effectiveRange,
		active,
		start,
		end,
		value,
		startDate,
		endDate,
		customFormatDate,
		minDate,
	]);

	const revertRangeDraftFromProps = () => {
		let s;
		if (start === null) {
			s = null;
		} else if (start) {
			s = new Date(start);
			if (isNaN(s.getTime())) {
				s = defaultRangeDraftStartDate();
			}
		} else if (value) {
			s = new Date(value);
			if (isNaN(s.getTime())) {
				s = defaultRangeDraftStartDate();
			}
		} else if (minDate) {
			s = new Date(minDate.getTime());
		} else if (filtersFooter) {
			s = null;
		} else {
			s = defaultRangeDraftStartDate();
		}

		const e =
				end !== undefined && end !== null
						? new Date(end)
						: null;
		const eOk = e && !isNaN(e.getTime());

		if (s && !isNaN(s.getTime())) {
			setStartDate(s);
		} else {
			setStartDate(null);
		}
		if (eOk) {
			setEndDate(e);
		} else {
			setEndDate(null);
		}
		if (start && end) {
			setRenderDate(`${customFormatDate(start)} - ${customFormatDate(end)}`);
		} else if (start && !end) {
			setRenderDate(customFormatDate(start));
		} else if (s && !isNaN(s.getTime())) {
			setRenderDate(
					eOk
							? `${customFormatDate(s)} - ${customFormatDate(e)}`
							: customFormatDate(s),
			);
		} else {
			setRenderDate("");
		}
	};

	const handleChange = useCallback(
			(dates) => {
					if (!dates) {
							return;
					}

					if (effectiveRange) {
							const [rangeStart, rangeEnd] = dates;
							setStartDate(rangeStart);
							setEndDate(rangeEnd);
							if (rangeStart && rangeEnd) {
									setRenderDate(
											`${customFormatDate(rangeStart)} - ${customFormatDate(rangeEnd)}`,
									);
							} else if (rangeStart) {
									setRenderDate(customFormatDate(rangeStart));
							} else {
									setRenderDate("");
							}
							if (applySelectionInstantly) {
									if (rangeStart && rangeEnd) {
											onChange(rangeStart, rangeEnd);
											setActive(false);
									} else if (rangeStart) {
											onChange(rangeStart, null);
									}
							}
							return;
					}

					setStartDate(dates);
					if (dates) {
							setRenderDate(customFormatDate(dates));
					} else {
							setRenderDate("");
					}
					if (applySelectionInstantly && dates) {
							onChange(dates, null);
							setActive(false);
					}
			},
			[
					effectiveRange,
					customFormatDate,
					applySelectionInstantly,
					onChange,
			],
	);

	const handleIndependentLeftCalendarChange = useCallback(
			(date) => {
				if (!effectiveRange || !decoupleRightPanelFromRangeStart) {
					return;
				}
				const deferExternalChangeUntilApply =
						filtersFooter && splitRangePanesMode;
				const picked = Array.isArray(date) ? date[0] : date;
				if (picked == null) {
					setStartDate(null);
					setEndDate(null);
					setRenderDate("");
					if (!deferExternalChangeUntilApply && applySelectionInstantly) {
							onChange(null, null);
					}
					return;
				}
				const clicked = moment(picked).startOf("day");
				const nextStart = picked;
				let nextEnd = endDate;
				if (clearEndOnIndependentLeftPick) {
						nextEnd = null;
				} else if (endDate) {
					const endM = moment(endDate).startOf("day");
					if (clicked.isAfter(endM)) {
							nextEnd = null;
					}
				}
				setStartDate(nextStart);
				setEndDate(nextEnd);
				if (filtersFooterDecoupledDualRange && nextStart) {
						const nextRightAnchor = nextEnd || nextStart;
						if (nextRightAnchor) {
								setRightPanelMonth(
										moment(nextRightAnchor).startOf("month").toDate(),
								);
								setRightPaneCalendarKey((k) => k + 1);
						}
						setLeftPanelMonth(
								moment(nextStart).startOf("month").toDate(),
						);
						setLeftPaneCalendarKey((k) => k + 1);
				}
				if (nextStart && nextEnd) {
					setRenderDate(
							`${customFormatDate(nextStart)} - ${customFormatDate(nextEnd)}`,
					);
				} else if (nextStart) {
					setRenderDate(customFormatDate(nextStart));
				} else {
					setRenderDate("");
				}
				if (deferExternalChangeUntilApply) {
						return;
				}
				if (applySelectionInstantly) {
						onChange(nextStart, nextEnd ?? null);
						if (effectiveRange && nextStart && nextEnd) {
								setActive(false);
						}
				}
			},
			[
				effectiveRange,
				decoupleRightPanelFromRangeStart,
				filtersFooterDecoupledDualRange,
				splitRangePanesMode,
				filtersFooter,
				endDate,
				customFormatDate,
				applySelectionInstantly,
				onChange,
				clearEndOnIndependentLeftPick,
			],
	);

	const handleIndependentRightCalendarChange = useCallback(
			(date) => {
				if (!effectiveRange || !decoupleRightPanelFromRangeStart) {
					return;
				}
				const deferExternalChangeUntilApply =
						filtersFooter && splitRangePanesMode;
				if (filtersFooterDecoupledDualRange) {
						const picked = Array.isArray(date) ? date[0] : date;
						if (!picked) {
								setEndDate(null);
								setRenderDate(startDate ? customFormatDate(startDate) : "");
								return;
						}

						if (!startDate) {
								setEndDate(picked);
								setRightPanelMonth(
										moment(picked).startOf("month").toDate(),
								);
								setRightPaneCalendarKey((k) => k + 1);
								setRenderDate(customFormatDate(picked));
								return;
						}

						const startM = moment(startDate).startOf("day");
						const pickedM = moment(picked).startOf("day");
						if (pickedM.isBefore(startM)) {
								return;
						}

						setEndDate(picked);
						setRightPanelMonth(moment(picked).startOf("month").toDate());
						setRightPaneCalendarKey((k) => k + 1);
						setRenderDate(
								`${customFormatDate(startDate)} - ${customFormatDate(picked)}`,
						);
						return;
				}
				const swapRange = Boolean(datepickerProps?.swapRange);
				if (date == null) {
					setEndDate(null);
					if (startDate) {
						setRenderDate(customFormatDate(startDate));
					} else {
						setRenderDate("");
					}
					if (!deferExternalChangeUntilApply && applySelectionInstantly) {
							onChange(startDate ?? null, null);
					}
					return;
				}
				const s = startDate;
				if (!s) {
					if (strictCalendarSplit) {
						return;
					}
					setStartDate(date);
					setEndDate(null);
					setRenderDate(customFormatDate(date));
					if (!deferExternalChangeUntilApply && applySelectionInstantly) {
							onChange(date, null);
							setActive(false);
					}
					return;
				}
				const clicked = moment(date).startOf("day");
				const startM = moment(s).startOf("day");
				let nextStart = s;
				let nextEnd = endDate;
				if (!endDate) {
					if (clicked.isBefore(startM)) {
						if (swapRange) {
							nextStart = date;
							nextEnd = s;
						} else {
							nextStart = date;
							nextEnd = null;
						}
					} else {
						nextEnd = date;
					}
				} else if (clicked.isBefore(startM)) {
					if (swapRange) {
						nextStart = date;
						nextEnd = s;
					} else {
						nextStart = date;
						nextEnd = null;
					}
				} else {
					nextEnd = date;
				}
				setStartDate(nextStart);
				setEndDate(nextEnd);
				if (filtersFooterDecoupledDualRange && nextEnd) {
						setRightPanelMonth(moment(nextEnd).startOf("month").toDate());
						setRightPaneCalendarKey((k) => k + 1);
				}
				if (nextStart && nextEnd) {
					setRenderDate(
							`${customFormatDate(nextStart)} - ${customFormatDate(nextEnd)}`,
					);
				} else if (nextStart) {
					setRenderDate(customFormatDate(nextStart));
				} else {
					setRenderDate("");
				}
				if (deferExternalChangeUntilApply) {
						return;
				}
				if (applySelectionInstantly) {
						onChange(nextStart, nextEnd ?? null);
						if (effectiveRange && nextStart && nextEnd) {
								setActive(false);
						}
				}
			},
			[
				effectiveRange,
				decoupleRightPanelFromRangeStart,
				filtersFooterDecoupledDualRange,
				splitRangePanesMode,
				filtersFooter,
				datepickerProps?.swapRange,
				startDate,
				endDate,
				customFormatDate,
				strictCalendarSplit,
				applySelectionInstantly,
				onChange,
			],
	);

	const mergeIndependentDualDayClassName = useCallback(
			(date, pane) => {
					const userRaw = datepickerProps?.dayClassName?.(date);
					const user = filtersFooterDecoupledDualRange
							? sanitizeDayClassNameForFooterDecouple(userRaw)
							: userRaw;
					if (!decoupleRightPanelFromRangeStart) {
							return user;
					}
					const rangeCls =
							effectiveRange
									? getIndependentDualRangeDayClassName(
											date,
											pane,
											startDate,
											endDate,
											splitRangePanesMode,
										)
									: undefined;
					const withRange = classNames(user, rangeCls);
					if (
							!suggestedRangeHintsMode ||
							startDate ||
							endDate
					) {
							return withRange;
					}
					const bundle = getHistoricalSuggestedHintBoundaries(
							historicalSuggestedRangePreset,
					);
					const anchorMonth =
							pane === "right"
									? bundle.rightAnchorMonth
									: bundle.leftAnchorMonth;
					const visibleMonth =
							pane === "right" ? rightPanelMonth : leftPanelMonth;
					const visibleMatchesSuggested =
							suggestedVisibleMonthKey(visibleMonth) ===
							suggestedMonthKey(anchorMonth);
					const cellIsExactSuggested =
							pane === "right"
									? isSameSuggestedCalendarDay(date, bundle.end) &&
										moment(date).isSame(bundle.rightAnchorMonth, "month")
									: isSameSuggestedCalendarDay(date, bundle.start) &&
										moment(date).isSame(bundle.leftAnchorMonth, "month");
					if (
							visibleMatchesSuggested &&
							cellIsExactSuggested &&
							isIndependentDualDaySelectable(date, pane)
					) {
							return classNames(
									withRange,
									"AdvancedRangeDatepicker__daySuggestedHint",
							);
					}
					return withRange;
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[
				datepickerProps?.dayClassName,
				decoupleRightPanelFromRangeStart,
				filtersFooterDecoupledDualRange,
				splitRangePanesMode,
				effectiveRange,
				startDate,
				endDate,
				suggestedRangeHintsMode,
				isIndependentDualDaySelectable,
				leftPanelMonth,
				rightPanelMonth,
				historicalSuggestedRangePreset,
			],
	);

	const mergeIndependentDualLeftDayClassName = useCallback(
			(date) => mergeIndependentDualDayClassName(date, "left"),
			[mergeIndependentDualDayClassName],
	);

	const mergeIndependentDualRightDayClassName = useCallback(
			(date) => mergeIndependentDualDayClassName(date, "right"),
			[mergeIndependentDualDayClassName],
	);

	const handleDecoupledLeftMonthYearChange = useCallback(
			(viewDate) => {
				const nextLeft = moment(viewDate).startOf("month").toDate();
				if (filtersFooterDecoupledDualRange) {
						setLeftPanelMonth(nextLeft);
						return;
				}
				setLeftPanelMonth(nextLeft);
				setRightPanelMonth((prevRight) => {
					const l = moment(nextLeft).startOf("month");
					const r = moment(prevRight).startOf("month");
					if (strictCalendarSplit) {
							if (r.isBefore(l)) {
									return l.toDate();
							}
							return prevRight;
					}
					if (r.isSameOrBefore(l)) {
							return l.clone().add(1, "month").toDate();
					}
					return prevRight;
				});
			},
			[
				filtersFooterDecoupledDualRange,
				strictCalendarSplit,
			],
	);

	const handleDecoupledRightMonthYearChange = useCallback(
			(viewDate) => {
				const nextR = moment(viewDate).startOf("month");
				if (filtersFooterDecoupledDualRange) {
						setRightPanelMonth(nextR.toDate());
						return;
				}
				const floor =
						strictCalendarSplit && startDate
								? moment(startDate).startOf("month")
								: moment(leftPanelMonth).startOf("month");
				if (strictCalendarSplit) {
						if (nextR.isBefore(floor)) {
								setRightPanelMonth(floor.toDate());
						} else {
								setRightPanelMonth(nextR.toDate());
						}
						return;
				}
				const l = moment(leftPanelMonth).startOf("month");
				if (nextR.isSameOrBefore(l)) {
						setRightPanelMonth(l.clone().add(1, "month").toDate());
				} else {
						setRightPanelMonth(nextR.toDate());
				}
			},
			[
				filtersFooterDecoupledDualRange,
				leftPanelMonth,
				startDate,
				strictCalendarSplit,
			],
	);

	const handleApplyRange = () => {
		if (!startDate || !endDate) {
			return;
		}
		onChange(startDate, endDate);
		setRenderDate(
				`${customFormatDate(startDate)} - ${customFormatDate(endDate)}`,
		);
		setRangeViewAnchor(null);
		setActive(false);
	};

	const handleApplySingle = () => {
		if (!startDate) {
			return;
		}
		onChange(startDate, null);
		setRenderDate(customFormatDate(startDate));
		setActive(false);
	};

	const resetDecoupledPanelsToPreviousMonth = useCallback(() => {
			if (!filtersFooterDecoupledDualRange) {
					return;
			}
			const prevMonth = moment()
					.subtract(1, "month")
					.startOf("month")
					.toDate();
			setLeftPanelMonth(prevMonth);
			setRightPanelMonth(prevMonth);
			setLeftPaneCalendarKey((k) => k + 1);
			setRightPaneCalendarKey((k) => k + 1);
	}, [filtersFooterDecoupledDualRange]);

	const onClear = () => {
		onChange(null, null);
		setEndDate(null);
		resetDecoupledPanelsToPreviousMonth();
		if (effectiveRange) {
			if (filtersFooter) {
				setStartDate(null);
				setRenderDate("");
			} else {
				const nextStart = defaultRangeDraftStartDate();
				setStartDate(nextStart);
				setRenderDate(customFormatDate(nextStart));
			}
		} else {
			setRenderDate("");
			setStartDate(new Date());
		}
	};

	const revertSingleDraftFromProps = () => {
		const s = value
				? new Date(value)
				: (start
						? new Date(start)
						: (minDate ? new Date(minDate.getTime()) : null));
		if (s && !isNaN(s.getTime())) {
			setStartDate(s);
			setRenderDate(customFormatDate(s));
		} else {
			setStartDate(null);
			setRenderDate("");
		}
		setEndDate(null);
	};

	const handleFiltersFooterReset = () => {
		setRangeViewAnchor(null);
		if (effectiveRange) {
			setStartDate(null);
			setEndDate(null);
			setRenderDate("");
			resetDecoupledPanelsToPreviousMonth();
			onChange(null, null);
		} else {
			revertSingleDraftFromProps();
		}
		setActive(false);
	};

	const handleRangeModeToggle = (checked) => {
		setRangeMode(checked);
		setRangeViewAnchor(null);
		if (!checked) {
			const d = startDate || endDate;
			setEndDate(null);
			if (d) {
				setStartDate(d);
				onChange(d, null);
				setRenderDate(customFormatDate(d));
			} else {
				revertSingleDraftFromProps();
			}
		} else {
			revertRangeDraftFromProps();
		}
	};

	const handleInputChange = (val) => {
		setRenderDate(val);
		if (effectiveRange) {
			return;
		}
		const parsedDate = new Date(val);
		if (!isNaN(parsedDate)) {
			setStartDate(parsedDate);
			if (!active) {
				onChange(parsedDate, null);
			}
		}
	};

	dismissPickerRef.current = () => {
		if (effectiveRange) {
			revertRangeDraftFromProps();
		} else {
			revertSingleDraftFromProps();
		}
		setRangeViewAnchor(null);
		setActive(false);
	};

	const handleTriggerMouseDown = useCallback(
			(e) => {
					if (disabled) {
						return;
					}
					if (e.button !== 0) {
						return;
					}
					if (
							e.target.closest?.(
									".AdvancedRangeDatepicker__nativeTriggerClear",
							)
					) {
						return;
					}
					if (active) {
							e.preventDefault();
							dismissPickerRef.current();
							triggerInputRef.current?.blur();
							return;
					}
					setActive(true);
					queueMicrotask(() => {
							triggerInputRef.current?.focus({
									preventScroll: true,
							});
					});
			},
			[active, disabled],
	);

	const openToDateMerged =
			rangeViewAnchor != null ? rangeViewAnchor : datepickerProps?.openToDate;

	const accessibleFieldLabel = labelDisabled
			? t("datepicker.dateRange")
			: resolvedTriggerLabel;
	const placeholderResolved =
			placeholder != null && String(placeholder).trim() !== ""
					? placeholder
					: accessibleFieldLabel;

	const triggerInputSize = useMemo(() => {
		const valueLen = String(renderDate || "").length;
		if (valueLen > 0) {
			return Math.min(Math.max(valueLen + 2, 14), 56);
		}
		const phLen = String(placeholderResolved || "").length;
		return Math.min(Math.max(phLen, 14), 44);
	}, [renderDate, placeholderResolved]);

	const showClearButton = useMemo(() => {
		if (!isClearable || disabled) {
			return false;
		}
		if (!effectiveRange) {
			return String(renderDate || "").trim() !== "";
		}
		const parentPassesRangeBounds =
				start !== undefined || end !== undefined;
		if (parentPassesRangeBounds) {
			return start != null && end != null;
		}
		return Boolean(startDate && endDate);
	}, [
		isClearable,
		disabled,
		effectiveRange,
		renderDate,
		start,
		end,
		startDate,
		endDate,
	]);

	const showYearDropdownEffective =
			datepickerProps?.showYearDropdown ?? true;

	const showSplitRangeSummary =
			effectiveRange &&
			(useIndependentDualMonths || useDualMonthRangeHeader);

	const startPaneSubtitleText = useMemo(() => {
		if (!showSplitRangeSummary) {
			return "";
		}
		return `${t("datepicker.selectedStartDate", {
				defaultValue: "Selected start date",
		})}: ${startDate ? customFormatDate(startDate) : ""}`;
	}, [showSplitRangeSummary, t, startDate, customFormatDate]);

	const endPaneSubtitleText = useMemo(() => {
		if (!showSplitRangeSummary) {
			return "";
		}
		return `${t("datepicker.selectedEndDate", {
				defaultValue: "Selected end date",
		})}: ${endDate ? customFormatDate(endDate) : ""}`;
	}, [showSplitRangeSummary, t, endDate, customFormatDate]);

	const pickerShowYearDropdown = Boolean(
			_dpRenderCustomHeader && showYearDropdownEffective,
	);

	const renderBuiltInPaneHeaderLeft = (hp) => (
			<AdvancedRangePaneHeader
					monthDate={hp.monthDate}
					decreaseMonth={hp.decreaseMonth}
					increaseMonth={hp.increaseMonth}
					decreaseYear={hp.decreaseYear}
					increaseYear={hp.increaseYear}
					prevMonthButtonDisabled={hp.prevMonthButtonDisabled}
					nextMonthButtonDisabled={hp.nextMonthButtonDisabled}
					prevYearButtonDisabled={hp.prevYearButtonDisabled}
					nextYearButtonDisabled={hp.nextYearButtonDisabled}
					prevAriaLabel={t("datepicker.prevMonth")}
					nextAriaLabel={t("datepicker.nextMonth")}
					prevYearAriaLabel={t("datepicker.prevYear", {
							defaultValue: "Previous years",
					})}
					nextYearAriaLabel={t("datepicker.nextYear", {
							defaultValue: "Next years",
					})}
					showSubtitle={showSplitRangeSummary}
					subtitleLine={startPaneSubtitleText}
					yearSelectAriaLabel={t("datepicker.chooseYear", {
							defaultValue: "Choose year",
					})}
					isYearGridOpen={yearPickerOpenStart}
					onToggleYearGrid={() => {
							setYearPickerOpenStart((prev) => {
									const next = !prev;
									if (next) {
											leftYearViewAnchorRef.current = moment(
													hp.monthDate,
											)
													.startOf("month")
													.toDate();
											setYearPickerOpenEnd(false);
									}
									return next;
							});
					}}
			/>
	);

	const renderBuiltInPaneHeaderRight = (hp) => (
			<AdvancedRangePaneHeader
					monthDate={hp.monthDate}
					decreaseMonth={hp.decreaseMonth}
					increaseMonth={hp.increaseMonth}
					decreaseYear={hp.decreaseYear}
					increaseYear={hp.increaseYear}
					prevMonthButtonDisabled={
							hp.prevMonthButtonDisabled ||
							(filtersFooterDecoupledDualRange
									? false
									: disableRightPanePrevMonthAdjacent ||
										(Boolean(strictCalendarSplit) &&
												strictRightNavFloorMonth != null &&
												moment(hp.monthDate)
														.startOf("month")
														.isSameOrBefore(
																strictRightNavFloorMonth,
														)))
					}
					nextMonthButtonDisabled={hp.nextMonthButtonDisabled}
					prevYearButtonDisabled={hp.prevYearButtonDisabled}
					nextYearButtonDisabled={hp.nextYearButtonDisabled}
					prevAriaLabel={t("datepicker.prevMonth")}
					nextAriaLabel={t("datepicker.nextMonth")}
					prevYearAriaLabel={t("datepicker.prevYear", {
							defaultValue: "Previous years",
					})}
					nextYearAriaLabel={t("datepicker.nextYear", {
							defaultValue: "Next years",
					})}
					showSubtitle={showSplitRangeSummary}
					subtitleLine={endPaneSubtitleText}
					yearSelectAriaLabel={t("datepicker.chooseYear", {
							defaultValue: "Choose year",
					})}
					isYearGridOpen={yearPickerOpenEnd}
					onToggleYearGrid={() => {
							setYearPickerOpenEnd((prev) => {
									const next = !prev;
									if (next) {
											rightYearViewAnchorRef.current = moment(
													hp.monthDate,
											)
													.startOf("month")
													.toDate();
											setYearPickerOpenStart(false);
									}
									return next;
							});
					}}
			/>
	);

	const renderBuiltInPaneHeaderSync = (hp) => (
			<AdvancedRangePaneHeader
					monthDate={hp.monthDate}
					decreaseMonth={hp.decreaseMonth}
					increaseMonth={hp.increaseMonth}
					decreaseYear={hp.decreaseYear}
					increaseYear={hp.increaseYear}
					prevMonthButtonDisabled={hp.prevMonthButtonDisabled}
					nextMonthButtonDisabled={hp.nextMonthButtonDisabled}
					prevYearButtonDisabled={hp.prevYearButtonDisabled}
					nextYearButtonDisabled={hp.nextYearButtonDisabled}
					prevAriaLabel={t("datepicker.prevMonth")}
					nextAriaLabel={t("datepicker.nextMonth")}
					prevYearAriaLabel={t("datepicker.prevYear", {
							defaultValue: "Previous years",
					})}
					nextYearAriaLabel={t("datepicker.nextYear", {
							defaultValue: "Next years",
					})}
					showSubtitle={showSplitRangeSummary}
					subtitleLine={
							showSplitRangeSummary
									? hp.customHeaderCount === 0
											? startPaneSubtitleText
											: endPaneSubtitleText
									: ""
					}
					yearSelectAriaLabel={t("datepicker.chooseYear", {
							defaultValue: "Choose year",
					})}
					isYearGridOpen={yearPickerOpen}
					onToggleYearGrid={() => {
							setYearPickerOpen((prev) => {
									const next = !prev;
									if (next) {
											syncYearViewAnchorRef.current = moment(
													hp.monthDate,
											)
													.startOf("month")
													.toDate();
									}
									return next;
							});
					}}
			/>
	);

	const splitSelectionSummaryRow = (
			<div className='AdvancedRangeDatepicker__selectionSummaryRow'>
					<div className='AdvancedRangeDatepicker__summaryCell'>
							<InlineStack
									gap='200'
									wrap={false}
									blockAlign='baseline'
									align='center'
							>
									<Text variant='bodySm' as='span' tone='subdued'>
											{t("datepicker.startDate")}
											{":"}
									</Text>
									<Text variant='bodyMd' as='span' fontWeight='semibold'>
											{startDate
													? customFormatDate(startDate)
													: ""}
									</Text>
							</InlineStack>
					</div>
					<div className='AdvancedRangeDatepicker__summaryCell'>
							<InlineStack
									gap='200'
									wrap={false}
									blockAlign='baseline'
									align='center'
							>
									<Text variant='bodySm' as='span' tone='subdued'>
											{t("datepicker.endDate")}
											{":"}
									</Text>
									<Text variant='bodyMd' as='span' fontWeight='semibold'>
											{endDate
													? customFormatDate(endDate)
													: ""}
									</Text>
							</InlineStack>
					</div>
			</div>
	);

	const suppressEmptyDualRangeKeyboardNav =
			filtersFooter &&
			effectiveRange &&
			useIndependentDualMonths &&
			!startDate &&
			!endDate;

	const suppressFiltersFooterLeftPartialRangeKeyboardNav =
			filtersFooter &&
			effectiveRange &&
			decoupleRightPanelFromRangeStart &&
			Boolean(startDate) &&
			!endDate;

	const handleLeftInlineChange = useCallback(
			(dates) => {
					if (yearPickerOpenStart) {
							setYearPickerOpenStart(false);
							const picked = Array.isArray(dates) ? dates[0] : dates;
							if (picked && !isNaN(picked.getTime())) {
									const y = moment(picked).year();
									const anchor = leftYearViewAnchorRef.current
											? moment(leftYearViewAnchorRef.current)
											: moment(leftPanelMonth).startOf("month");
									setLeftPanelMonth(
											anchor.year(y).startOf("month").toDate(),
									);
									leftYearGridRemountRef.current = true;
									setLeftPaneCalendarKey((k) => k + 1);
							}
							return;
					}
					if (decoupleRightPanelFromRangeStart) {
							handleIndependentLeftCalendarChange(dates);
					} else {
							handleChange(dates);
					}
			},
			[
					decoupleRightPanelFromRangeStart,
					handleChange,
					handleIndependentLeftCalendarChange,
					yearPickerOpenStart,
					leftPanelMonth,
			],
	);

	const handleRightInlineChange = useCallback(
			(dates) => {
					if (yearPickerOpenEnd) {
							setYearPickerOpenEnd(false);
							const picked = Array.isArray(dates) ? dates[0] : dates;
							if (picked && !isNaN(picked.getTime())) {
									const y = moment(picked).year();
									const anchor = rightYearViewAnchorRef.current
											? moment(rightYearViewAnchorRef.current)
											: moment(rightPanelMonth).startOf("month");
									setRightPanelMonth(
											anchor.year(y).startOf("month").toDate(),
									);
									rightYearGridRemountRef.current = true;
									setRightPaneCalendarKey((k) => k + 1);
							}
							return;
					}
					if (decoupleRightPanelFromRangeStart) {
							handleIndependentRightCalendarChange(dates);
					} else {
							handleChange(dates);
					}
			},
			[
					decoupleRightPanelFromRangeStart,
					handleChange,
					handleIndependentRightCalendarChange,
					yearPickerOpenEnd,
					rightPanelMonth,
			],
	);

	const handleSingleOrSyncInlineChange = useCallback(
			(dates) => {
					if (yearPickerOpen) {
							setYearPickerOpen(false);
							const picked = Array.isArray(dates) ? dates[0] : dates;
							if (picked && !isNaN(picked.getTime())) {
									const y = moment(picked).year();
									const anchor = syncYearViewAnchorRef.current
											? moment(syncYearViewAnchorRef.current)
											: moment(
															openToDateMerged ??
																	startDate ??
																	endDate ??
																	new Date(),
													).startOf("month");
									setRangeViewAnchor(
											anchor.year(y).startOf("month").toDate(),
									);
									syncYearGridRemountRef.current = true;
									setSyncPaneCalendarKey((k) => k + 1);
							}
							return;
					}
					handleChange(dates);
			},
			[
					handleChange,
					yearPickerOpen,
					openToDateMerged,
					startDate,
					endDate,
			],
	);

	const leftCalendarKey = `ard-left-${leftPaneCalendarKey}-${moment(
			leftPanelMonth,
	).format("YYYY-MM")}`;
	const rightCalendarKey = `ard-right-${rightPaneCalendarKey}-${moment(
			rightPanelMonth,
	).format("YYYY-MM")}`;

	const calendarNode = useIndependentDualMonths ? (
			<div className='AdvancedRangeDatepicker__dualIndependent'>
					<div className='AdvancedRangeDatepicker__dualPane AdvancedRangeDatepicker__dualPane--start'>
							<ReactDatePicker
							{...restDatepickerProps}
							calendarStartDay={calendarStartDayMerged}
							selected={
									filtersFooterDecoupledDualRange ? null : startDate
							}
							onChange={
									_dpRenderCustomHeader
											? decoupleRightPanelFromRangeStart
													? handleIndependentLeftCalendarChange
													: handleChange
											: handleLeftInlineChange
							}
							showYearPicker={
									_dpRenderCustomHeader
											? Boolean(_datepickerShowYearPicker)
											: yearPickerOpenStart
							}
							startDate={
									filtersFooterDecoupledDualRange
											? undefined
											: decoupleRightPanelFromRangeStart
											? undefined
											: useSplitPaneSelection
													? endDate
															? startDate
															: undefined
													: startDate
							}
							endDate={
									filtersFooterDecoupledDualRange
											? undefined
											: decoupleRightPanelFromRangeStart
											? undefined
											: useSplitPaneSelection
													? endDate
															? endDate
															: undefined
													: endDate
							}
							selectsStart={
									filtersFooterDecoupledDualRange
											? undefined
											: decoupleRightPanelFromRangeStart
											? undefined
											: useSplitPaneSelection && endDate
													? true
													: undefined
							}
							inline
							selectsRange={
									filtersFooterDecoupledDualRange
											? undefined
											: decoupleRightPanelFromRangeStart
											? false
											: !useSplitPaneSelection
							}
							shouldCloseOnSelect={false}
							disabledKeyboardNavigation={
									suppressEmptyDualRangeKeyboardNav ||
									suppressFiltersFooterLeftPartialRangeKeyboardNav ||
									Boolean(
											restDatepickerProps.disabledKeyboardNavigation,
									)
							}
							minDate={minDate}
							maxDate={effectiveMaxDate}
							filterDate={independentDualLeftFilter}
							monthsShown={1}
							showYearDropdown={pickerShowYearDropdown}
							renderCustomHeader={
									_dpRenderCustomHeader ??
									renderBuiltInPaneHeaderLeft
							}
							{...(decoupleRightPanelFromRangeStart
									? {
											dayClassName:
													mergeIndependentDualLeftDayClassName,
										}
									: {})}
							onMonthChange={(d) => {
									restDatepickerProps.onMonthChange?.(d);
									if (decoupleRightPanelFromRangeStart) {
										handleDecoupledLeftMonthYearChange(d);
									} else {
										setLeftPanelMonth(
												moment(d)
														.startOf("month")
														.toDate(),
										);
									}
							}}
							onYearChange={(d) => {
									restDatepickerProps.onYearChange?.(d);
									if (!_dpRenderCustomHeader && yearPickerOpenStart) {
											return;
									}
									if (decoupleRightPanelFromRangeStart) {
										handleDecoupledLeftMonthYearChange(d);
									} else {
										setLeftPanelMonth(
												moment(d)
														.startOf("month")
														.toDate(),
										);
									}
							}}
							openToDate={
									omitLeftInlineOpenToDate
											? undefined
											: leftPanelMonth
							}
							allowSameDay={
									effectiveRange
											? restDatepickerProps.allowSameDay !==
													false
											: restDatepickerProps.allowSameDay
							}
							key={leftCalendarKey}
							/>
					</div>
					<div className='AdvancedRangeDatepicker__dualPane AdvancedRangeDatepicker__dualPane--end'>
							<ReactDatePicker
							{...restDatepickerProps}
							calendarStartDay={calendarStartDayMerged}
							selected={
									filtersFooterDecoupledDualRange ? null : endDate
							}
							onChange={
									_dpRenderCustomHeader
											? decoupleRightPanelFromRangeStart
													? handleIndependentRightCalendarChange
													: handleChange
											: handleRightInlineChange
							}
							showYearPicker={
									_dpRenderCustomHeader
											? Boolean(_datepickerShowYearPicker)
											: yearPickerOpenEnd
							}
							startDate={
									filtersFooterDecoupledDualRange
											? undefined
											: decoupleRightPanelFromRangeStart
											? undefined
											: startDate
							}
							endDate={
									filtersFooterDecoupledDualRange
											? undefined
											: decoupleRightPanelFromRangeStart
											? undefined
											: endDate
							}
							inline
							selectsRange={
									filtersFooterDecoupledDualRange
											? undefined
											: !decoupleRightPanelFromRangeStart
							}
							selectsEnd={undefined}
							shouldCloseOnSelect={false}
							disabledKeyboardNavigation={
									suppressEmptyDualRangeKeyboardNav ||
									Boolean(
											restDatepickerProps.disabledKeyboardNavigation,
									)
							}
							minDate={
									decoupleRightPanelFromRangeStart
											? rightCalendarMinDate
											: minDate
							}
							maxDate={effectiveMaxDate}
							filterDate={independentDualRightFilter}
							monthsShown={1}
							showYearDropdown={pickerShowYearDropdown}
							renderCustomHeader={
									_dpRenderCustomHeader ??
									renderBuiltInPaneHeaderRight
							}
							{...(decoupleRightPanelFromRangeStart
									? {
											dayClassName:
													mergeIndependentDualRightDayClassName,
										}
									: {})}
							onMonthChange={(d) => {
									restDatepickerProps.onMonthChange?.(d);
									if (decoupleRightPanelFromRangeStart) {
										handleDecoupledRightMonthYearChange(d);
									} else {
										handleLinkedRightMonthOrYearChange(d);
									}
							}}
							onYearChange={(d) => {
									restDatepickerProps.onYearChange?.(d);
									if (!_dpRenderCustomHeader && yearPickerOpenEnd) {
											return;
									}
									if (decoupleRightPanelFromRangeStart) {
										handleDecoupledRightMonthYearChange(d);
									} else {
										handleLinkedRightMonthOrYearChange(d);
									}
							}}
							openToDate={
									omitRightInlineOpenToDate
											? undefined
											: rightPanelMonth
							}
							allowSameDay={
									effectiveRange
											? restDatepickerProps.allowSameDay !==
													false
											: restDatepickerProps.allowSameDay
							}
							key={rightCalendarKey}
							/>
					</div>
			</div>
	) : (
			<ReactDatePicker
					selected={startDate}
					onChange={
							_dpRenderCustomHeader
									? handleChange
									: handleSingleOrSyncInlineChange
					}
					startDate={startDate}
					endDate={endDate}
					inline
					selectsRange={effectiveRange}
					minDate={minDate}
					{...restDatepickerProps}
					maxDate={effectiveMaxDate}
					filterDate={
							restrictFutureEffective || userFilterDateFromProps
									? (d) => {
													if (
															userFilterDateFromProps &&
															!userFilterDateFromProps(d)
													) {
															return false;
													}
													return futureFilterDate(d);
												}
									: restDatepickerProps.filterDate
					}
					calendarStartDay={calendarStartDayMerged}
					monthsShown={monthsShownEffective}
					showYearPicker={
							_dpRenderCustomHeader
									? Boolean(_datepickerShowYearPicker)
									: yearPickerOpen
					}
					showYearDropdown={pickerShowYearDropdown}
					renderCustomHeader={
							_dpRenderCustomHeader ?? renderBuiltInPaneHeaderSync
					}
					openToDate={
							omitSyncInlineOpenToDate
									? undefined
									: (openToDateMerged ?? undefined)
					}
					shouldCloseOnSelect={false}
					allowSameDay={
							effectiveRange
									? restDatepickerProps.allowSameDay !== false
									: restDatepickerProps.allowSameDay
					}
					key={`ard-sync-${syncPaneCalendarKey}`}
			/>
	);

	return (
			<div
					className={classNames(
							"Datepicker",
							"AdvancedRangeDatepicker",
							active &&
									!disabled &&
									"AdvancedRangeDatepicker--isActive",
							(useDualMonthRangeHeader || useIndependentDualMonths) &&
									"AdvancedRangeDatepicker--dualMonths",
							useIndependentDualMonths &&
									"AdvancedRangeDatepicker--independentMonths",
							filtersFooter &&
									showFooter &&
									"AdvancedRangeDatepicker--filtersFooter",
					)}
					ref={pickerRef}
			>
				<div
						className='AdvancedRangeDatepicker__triggerShell'
						onMouseDown={handleTriggerMouseDown}
				>
					<div className='AdvancedRangeDatepicker__nativeTrigger'>
							{prefix ? (
									<span
											className='AdvancedRangeDatepicker__nativeTriggerPrefix'
											aria-hidden
									>
											{prefix}
									</span>
							) : null}
							<input
									ref={triggerInputRef}
									type='text'
									className='AdvancedRangeDatepicker__nativeTriggerInput'
									size={triggerInputSize}
									value={renderDate || ""}
									readOnly
									onChange={(e) => handleInputChange(e.target.value)}
									onFocus={() => {
											if (!disabled) {
													setActive(true);
											}
									}}
									onKeyDown={(e) => {
											if (e.key === "Backspace" || e.key === "Delete") {
													e.preventDefault();
											}
									}}
									onPaste={(e) => {
											e.preventDefault();
									}}
									placeholder={placeholderResolved}
									disabled={disabled}
									aria-label={accessibleFieldLabel}
									autoComplete='off'
							/>
							{showClearButton ? (
									<button
											type='button'
											className='AdvancedRangeDatepicker__nativeTriggerClear'
											onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													onClear();
											}}
											aria-label={t("datepicker.clear", {
													defaultValue: "Clear",
											})}
									>
											<img
													src={clearIMG}
													alt=''
													width={16}
													height={16}
											/>
									</button>
							) : null}
					</div>
				</div>
				{active && !disabled && (
						<div
								className='Datepicker-Wrapper'
								style={{[alignment]: 0}}
						>
							<BlockStack gap='300'>
									{showSplitRangeSummary ? (
											<div className='AdvancedRangeDatepicker__dualRangeLayout'>
													{_dpRenderCustomHeader
															? splitSelectionSummaryRow
															: null}
													{calendarNode}
											</div>
									) : (
											<>
													<div
															className={classNames(
																	"AdvancedRangeDatepicker__selectionSummary",
																	!effectiveRange &&
																			"AdvancedRangeDatepicker__selectionSummary--singleCentered",
															)}
													>
															{effectiveRange ? (
																	<InlineStack
																			gap='600'
																			wrap={false}
																			blockAlign='baseline'
																			align='space-between'
																	>
																			<InlineStack
																					gap='200'
																					wrap={false}
																					blockAlign='baseline'
																			>
																					<Text
																							variant='bodySm'
																							as='span'
																							tone='subdued'
																					>
																							{t(
																									"datepicker.startDate",
																							)}
																							{":"}
																					</Text>
																					<Text
																							variant='bodyMd'
																							as='span'
																							fontWeight='semibold'
																					>
																							{startDate
																									? customFormatDate(
																											startDate,
																										)
																									: ""}
																					</Text>
																			</InlineStack>
																			<InlineStack
																					gap='200'
																					wrap={false}
																					blockAlign='baseline'
																			>
																					<Text
																							variant='bodySm'
																							as='span'
																							tone='subdued'
																					>
																							{t(
																									"datepicker.endDate",
																							)}
																							{":"}
																					</Text>
																					<Text
																							variant='bodyMd'
																							as='span'
																							fontWeight='semibold'
																					>
																							{endDate
																									? customFormatDate(
																											endDate,
																										)
																									: ""}
																					</Text>
																			</InlineStack>
																	</InlineStack>
															) : (
																	<InlineStack
																			gap='200'
																			wrap={false}
																			blockAlign='baseline'
																			align='center'
																	>
																			<Text
																					variant='bodySm'
																					as='span'
																					tone='subdued'
																			>
																					{resolvedTriggerLabel}
																					{":"}
																			</Text>
																			<Text
																					variant='bodyMd'
																					as='span'
																					fontWeight='semibold'
																			>
																					{startDate
																							? customFormatDate(
																									startDate,
																								)
																							: t(
																									"datepicker.periodNotChosen",
																								)}
																			</Text>
																	</InlineStack>
															)}
													</div>
													{calendarNode}
											</>
									)}
									{inlineDropdownToggle && (
											<div
													className='AdvancedRangeDatepicker__extraToggle'
											>
													<Checkbox
															label={inlineDropdownToggle.label}
															checked={inlineDropdownToggle.checked}
															onChange={inlineDropdownToggle.onChange}
													/>
											</div>
									)}
									{showFooter ? (
											<div
													className={classNames(
															"AdvancedRangeDatepicker__footerRow",
															filtersFooter &&
																	"AdvancedRangeDatepicker__footerRow--filters",
													)}
											>
													{!filtersFooter ? (
															<div className='AdvancedRangeDatepicker__footerRowStart'>
																	{allowRange !== false &&
																			!hideRangeModeToggle && (
																					<Checkbox
																							label={t(
																									"datepicker.rangeOrDateToggle",
																							)}
																							checked={
																									rangeMode
																							}
																							onChange={
																									handleRangeModeToggle
																							}
																					/>
																			)}
															</div>
													) : null}
													<div
															className={classNames(
																	"AdvancedRangeDatepicker__footerRowEnd",
																	filtersFooter &&
																			"AdvancedRangeDatepicker__footerRowEnd--filters",
															)}
													>
															{filtersFooter ? (
																	<>
																			<button
																					type='button'
																					className='AdvancedRangeDatepicker__footerBtn AdvancedRangeDatepicker__footerBtn--reset'
																					onClick={
																							handleFiltersFooterReset
																					}
																			>
																					{resolvedResetButtonLabel}
																			</button>
																			<button
																					type='button'
																					className='AdvancedRangeDatepicker__footerBtn AdvancedRangeDatepicker__footerBtn--apply'
																					disabled={
																							effectiveRange
																									? !startDate ||
																											!endDate
																									: !startDate
																					}
																					onClick={
																							effectiveRange
																									? handleApplyRange
																									: handleApplySingle
																					}
																			>
																					{
																							resolvedApplyFiltersButtonLabel
																					}
																			</button>
																	</>
															) : effectiveRange ? (
																	<Button
																			onClick={handleApplyRange}
																			variant='primary'
																			disabled={
																					!startDate ||
																					!endDate
																			}
																	>
																			{resolvedApplyFooterButtonLabel}
																	</Button>
															) : (
																	<Button
																			onClick={handleApplySingle}
																			variant='primary'
																			disabled={!startDate}
																	>
																			{resolvedApplyFooterButtonLabel}
																	</Button>
															)}
													</div>
											</div>
									) : null}
							</BlockStack>
						</div>
				)}
			</div>
	);
};

AdvancedRangeDatepicker.propTypes = {
	onChange: PropTypes.func,
	label: PropTypes.string,
	triggerLabel: PropTypes.string,
	showFooter: PropTypes.bool,
	resetButtonLabel: PropTypes.string,
	applyButtonLabel: PropTypes.string,
	instantApplyWhenNoFooter: PropTypes.bool,
	prefix: PropTypes.node,
	start: PropTypes.string,
	end: PropTypes.string,
	value: PropTypes.string,
	allowRange: PropTypes.bool,
	placeholder: PropTypes.string,
	datepickerProps: PropTypes.object,
	isClearable: PropTypes.bool,
	customFormatDate: PropTypes.func,
	alignment: PropTypes.oneOf(["right", "left"]),
	disabled: PropTypes.bool,
	inlineDropdownToggle: PropTypes.shape({
		checked: PropTypes.bool.isRequired,
		onChange: PropTypes.func.isRequired,
		label: PropTypes.node,
	}),
	hideRangeModeToggle: PropTypes.bool,
	filtersFooter: PropTypes.bool,
	syncDualMonthNavigation: PropTypes.bool,
	linkDualMonthViewToRangeStart: PropTypes.bool,
		historicalRangeMode: PropTypes.bool,
		historicalSuggestedRangePreset: PropTypes.oneOf([
				HISTORICAL_SUGGESTED_RANGE_PREVIOUS_FULL_MONTH,
				HISTORICAL_SUGGESTED_RANGE_TRAILING_TO_TODAY,
		]),
		restrictFutureDates: PropTypes.bool,
	defaultOpenToPreviousMonth: PropTypes.bool,
	defaultRangeToPreviousMonth: PropTypes.bool,
	suggestedPreviousMonthRangeHints: PropTypes.bool,
	clearEndOnIndependentLeftPick: PropTypes.bool,
	separateCalendarSelection: PropTypes.bool,
	splitRangePanes: PropTypes.bool,
	onPickerActiveChange: PropTypes.func,
	minDate: PropTypes.oneOfType([
			PropTypes.instanceOf(Date),
			PropTypes.object,
			PropTypes.number,
	]),
	maxDate: PropTypes.oneOfType([
			PropTypes.instanceOf(Date),
			PropTypes.object,
			PropTypes.number,
	]),
	minDateOverride: PropTypes.oneOfType([
			PropTypes.instanceOf(Date),
			PropTypes.object,
			PropTypes.number,
	]),
	maxDateOverride: PropTypes.oneOfType([
			PropTypes.instanceOf(Date),
			PropTypes.object,
			PropTypes.number,
	]),
};

export default AdvancedRangeDatepicker;
